/**
 * PricingSettingsTab.js - Owner Dashboard Pricing Configuration
 * Allows owner to configure all service pricing, fuel settings, and business rules
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, Fuel, Car, Shirt, Package, ShoppingBag,
  Save, RefreshCw, AlertTriangle, Check, ChevronDown, ChevronRight,
  Settings, Calculator, Percent, Clock, MapPin, Scale, Info,
  TrendingUp, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { pricingAPI, ridesAPI } from '../../services/api';

const PricingSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [ridesPricing, setRidesPricing] = useState(null); // Separate state for rides pricing
  const [expandedSections, setExpandedSections] = useState({
    fuel: true,
    rides: false,
    laundry: false,
    courier: false,
    errands: false,
    business: false
  });
  const [testCalculation, setTestCalculation] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch general pricing config
      const response = await pricingAPI.getConfig();
      setConfig(response.data);
      
      // Fetch rides pricing separately (from the correct endpoint)
      try {
        const ridesResponse = await ridesAPI.getPricing();
        setRidesPricing(ridesResponse.data);
      } catch (e) {
        // Default rides pricing if not set
        setRidesPricing({
          base_fare: 5.00,
          per_mile_rate: 2.00,
          minimum_fare: 10.00,
          suv_multiplier: 1.25,
          van_multiplier: 1.50,
          wheelchair_multiplier: 1.30,
          night_surcharge: 5.00,
          weekend_surcharge: 4.00,
          busy_fee: 4.00,
          holiday_surcharge: 10.00,
          wait_time_per_minute: 0.50,
          extra_stop_fee: 5.00,
          assistance_fee: 5.00,
          driver_pay_percentage: 85.0
        });
      }
    } catch (error) {
      toast.error('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateConfigField = (path, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      if (section === 'fuel_settings') {
        await pricingAPI.updateFuelSettings(config.fuel_settings);
      } else if (section === 'rides') {
        // Save rides pricing to the correct endpoint that affects actual fare calculations
        await ridesAPI.updatePricing(ridesPricing);
      } else if (['laundry', 'courier', 'errands'].includes(section)) {
        await pricingAPI.updateServicePricing(section, config[section]);
      } else {
        await pricingAPI.updateConfig({ [section]: config[section] });
      }
      toast.success(`${section.replace('_', ' ')} settings saved`);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveAllSettings = async () => {
    setSaving(true);
    try {
      // Save general config
      await pricingAPI.updateConfig(config);
      // Save rides pricing separately to the correct endpoint
      await ridesAPI.updatePricing(ridesPricing);
      toast.success('All pricing settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Helper to update rides pricing fields
  const updateRidesPricingField = (field, value) => {
    setRidesPricing(prev => ({ ...prev, [field]: value }));
  };

  const testRidePrice = async () => {
    try {
      // Use the actual fare estimate endpoint to test with current pricing
      const response = await ridesAPI.estimateFare({
        pickup_zip: '27601',
        dropoff_zip: '27511',
        vehicle_type: 'sedan',
        passenger_count: 1,
        is_same_day: false
      });
      setTestCalculation({ type: 'ride', data: response.data });
      toast.success('Test calculation complete');
    } catch (error) {
      toast.error('Failed to calculate test price');
    }
  };

  // Section Header Component
  const SectionHeader = ({ icon: Icon, title, section, color }) => (
    <div 
      onClick={() => toggleSection(section)}
      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
        expandedSections[section] ? `bg-${color}-500/20 border border-${color}-500/30` : 'bg-[var(--background)] hover:bg-[var(--card)]'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${color}-500/20`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        <span className="font-medium text-[var(--text-primary)]">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronDown className="w-5 h-5 text-[var(--text-muted)]" />
      ) : (
        <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
      )}
    </div>
  );

  // Price Input Component
  const PriceInput = ({ label, value, onChange, prefix = '$', suffix = '', min = 0, step = 0.01, info }) => (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label className="text-sm text-[var(--text-secondary)]">{label}</Label>
        {info && (
          <div className="group relative">
            <Info className="w-3.5 h-3.5 text-[var(--text-muted)] cursor-help" />
            <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-[var(--card)] border border-[var(--border)] rounded text-xs text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {info}
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">{prefix}</span>
        )}
        <Input
          type="number"
          min={min}
          step={step}
          value={value || 0}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className={`bg-[var(--background)] ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-12' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">{suffix}</span>
        )}
      </div>
    </div>
  );

  // Percentage Input Component
  const PercentInput = ({ label, value, onChange, info }) => (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label className="text-sm text-[var(--text-secondary)]">{label}</Label>
        {info && (
          <div className="group relative">
            <Info className="w-3.5 h-3.5 text-[var(--text-muted)] cursor-help" />
            <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-[var(--card)] border border-[var(--border)] rounded text-xs text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {info}
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <Input
          type="number"
          min={0}
          max={1}
          step={0.01}
          value={value || 0}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="bg-[var(--background)] pr-8"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">
          ({Math.round((value || 0) * 100)}%)
        </span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-20">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <p className="text-[var(--text-muted)]">Failed to load pricing configuration</p>
        <Button onClick={fetchConfig} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-primary)]">Pricing Configuration</h2>
          <p className="text-sm text-[var(--text-muted)]">Configure service rates, fuel costs, and business settings</p>
        </div>
        <Button onClick={saveAllSettings} disabled={saving} className="btn-primary" data-testid="save-all-pricing-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>

      {/* Fuel Cost Alert */}
      {config.fuel_settings?.gas_price_per_gallon >= 4.00 && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-amber-400 font-medium">High Fuel Costs</p>
            <p className="text-sm text-[var(--text-muted)]">
              Current gas price (${config.fuel_settings.gas_price_per_gallon}/gal) triggers a {Math.round((config.fuel_settings.fuel_surcharge_tiers?.['4.00_4.49'] || 0.10) * 100)}% fuel surcharge on all mileage-based services.
            </p>
          </div>
        </div>
      )}

      {/* Fuel Settings Section */}
      <div className="space-y-3">
        <SectionHeader icon={Fuel} title="Fuel Cost Settings" section="fuel" color="orange" />
        {expandedSections.fuel && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg bg-[var(--card)] border border-[var(--border)] space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="NC Gas Price (per gallon)"
                value={config.fuel_settings?.gas_price_per_gallon}
                onChange={(v) => updateConfigField('fuel_settings.gas_price_per_gallon', v)}
                info="Current fuel price reference"
              />
              <PriceInput
                label="Vehicle MPG"
                value={config.fuel_settings?.vehicle_mpg}
                onChange={(v) => updateConfigField('fuel_settings.vehicle_mpg', v)}
                prefix=""
                suffix="mpg"
                info="Average vehicle fuel efficiency"
              />
              <PriceInput
                label="Mileage Reimbursement Rate"
                value={config.fuel_settings?.mileage_reimbursement_rate}
                onChange={(v) => updateConfigField('fuel_settings.mileage_reimbursement_rate', v)}
                suffix="/mi"
                info="IRS standard rate: $0.67/mi"
              />
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--background)]">
              <Switch
                checked={config.fuel_settings?.fuel_surcharge_enabled}
                onCheckedChange={(v) => updateConfigField('fuel_settings.fuel_surcharge_enabled', v)}
              />
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Automatic Fuel Surcharge</p>
                <p className="text-xs text-[var(--text-muted)]">Automatically add surcharge based on current gas prices</p>
              </div>
            </div>

            {config.fuel_settings?.fuel_surcharge_enabled && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-3 rounded-lg bg-green-500/10 text-center">
                  <p className="text-xs text-green-400">Below $3.00</p>
                  <p className="text-lg font-bold text-green-400">0%</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10 text-center">
                  <p className="text-xs text-green-400">$3.00-$3.49</p>
                  <p className="text-lg font-bold text-green-400">0%</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-500/10 text-center">
                  <p className="text-xs text-yellow-400">$3.50-$3.99</p>
                  <p className="text-lg font-bold text-yellow-400">5%</p>
                </div>
                <div className="p-3 rounded-lg bg-orange-500/10 text-center">
                  <p className="text-xs text-orange-400">$4.00-$4.49</p>
                  <p className="text-lg font-bold text-orange-400">10%</p>
                </div>
                <div className="p-3 rounded-lg bg-red-500/10 text-center">
                  <p className="text-xs text-red-400">$4.50+</p>
                  <p className="text-lg font-bold text-red-400">15%</p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={() => saveSection('fuel_settings')} disabled={saving} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save Fuel Settings
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Rides Pricing Section */}
      <div className="space-y-3">
        <SectionHeader icon={Car} title="Rides Pricing" section="rides" color="blue" />
        {expandedSections.rides && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg bg-[var(--card)] border border-[var(--border)] space-y-4"
          >
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 mb-4">
              <p className="text-sm text-blue-400">
                <strong>Note:</strong> Changes here directly affect customer ride pricing. 
                Customers will see the new rates immediately on quotes and checkout.
              </p>
            </div>
            
            <h4 className="font-medium text-[var(--text-primary)]">Base Pricing</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Base Fare"
                value={ridesPricing?.base_fare}
                onChange={(v) => updateRidesPricingField('base_fare', v)}
                info="Starting fare for all rides"
              />
              <PriceInput
                label="Per Mile Rate"
                value={ridesPricing?.per_mile_rate}
                onChange={(v) => updateRidesPricingField('per_mile_rate', v)}
                suffix="/mi"
                info="Rate charged per mile"
              />
              <PriceInput
                label="Minimum Fare"
                value={ridesPricing?.minimum_fare}
                onChange={(v) => updateRidesPricingField('minimum_fare', v)}
                info="Minimum charge for any ride"
              />
              <PriceInput
                label="Wait Time Rate"
                value={ridesPricing?.wait_time_per_minute}
                onChange={(v) => updateRidesPricingField('wait_time_per_minute', v)}
                suffix="/min"
                info="After 5 min free wait"
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Vehicle Multipliers</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-sm text-[var(--text-secondary)]">SUV Multiplier</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={3}
                    step={0.05}
                    value={ridesPricing?.suv_multiplier || 1.25}
                    onChange={(e) => updateRidesPricingField('suv_multiplier', parseFloat(e.target.value) || 1.25)}
                    className="bg-[var(--background)] pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">x</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-[var(--text-secondary)]">Van Multiplier</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={3}
                    step={0.05}
                    value={ridesPricing?.van_multiplier || 1.50}
                    onChange={(e) => updateRidesPricingField('van_multiplier', parseFloat(e.target.value) || 1.50)}
                    className="bg-[var(--background)] pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">x</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-[var(--text-secondary)]">Wheelchair Multiplier</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={3}
                    step={0.05}
                    value={ridesPricing?.wheelchair_multiplier || 1.30}
                    onChange={(e) => updateRidesPricingField('wheelchair_multiplier', parseFloat(e.target.value) || 1.30)}
                    className="bg-[var(--background)] pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">x</span>
                </div>
              </div>
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Surcharges</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Night Surcharge"
                value={ridesPricing?.night_surcharge}
                onChange={(v) => updateRidesPricingField('night_surcharge', v)}
                info="After 10 PM / Before 6 AM"
              />
              <PriceInput
                label="Weekend Surcharge"
                value={ridesPricing?.weekend_surcharge}
                onChange={(v) => updateRidesPricingField('weekend_surcharge', v)}
                info="Saturday & Sunday"
              />
              <PriceInput
                label="Same-Day / Rush Fee"
                value={ridesPricing?.busy_fee}
                onChange={(v) => updateRidesPricingField('busy_fee', v)}
                info="Same-day bookings"
              />
              <PriceInput
                label="Holiday Surcharge"
                value={ridesPricing?.holiday_surcharge}
                onChange={(v) => updateRidesPricingField('holiday_surcharge', v)}
                info="Major holidays"
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Additional Fees</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PriceInput
                label="Extra Stop Fee"
                value={ridesPricing?.extra_stop_fee}
                onChange={(v) => updateRidesPricingField('extra_stop_fee', v)}
                info="Per additional stop"
              />
              <PriceInput
                label="Assistance Fee"
                value={ridesPricing?.assistance_fee}
                onChange={(v) => updateRidesPricingField('assistance_fee', v)}
                info="Door-to-door assistance"
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Driver Pay</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm text-[var(--text-secondary)]">Driver Pay Percentage</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={50}
                    max={100}
                    step={1}
                    value={ridesPricing?.driver_pay_percentage || 85}
                    onChange={(e) => updateRidesPricingField('driver_pay_percentage', parseFloat(e.target.value) || 85)}
                    className="bg-[var(--background)] pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">%</span>
                </div>
                <p className="text-xs text-[var(--text-muted)]">Driver receives {ridesPricing?.driver_pay_percentage || 85}% of fare</p>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <Button onClick={testRidePrice} variant="outline" size="sm">
                <Calculator className="w-4 h-4 mr-1" /> Test Calculate
              </Button>
              <Button onClick={() => saveSection('rides')} disabled={saving} size="sm" data-testid="save-rides-pricing-btn">
                <Save className="w-4 h-4 mr-1" /> Save Rides Pricing
              </Button>
            </div>

            {testCalculation?.type === 'ride' && (
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 mt-4">
                <h5 className="font-medium text-blue-400 mb-2">Test Calculation: 15 mile ride</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-[var(--text-muted)]">Customer Price</p>
                    <p className="text-xl font-bold text-green-400">${testCalculation.data.customer_price || testCalculation.data.estimated_fare}</p>
                  </div>
                  <div>
                    <p className="text-[var(--text-muted)]">Distance</p>
                    <p className="text-lg font-medium text-blue-400">{testCalculation.data.estimated_miles || 15} mi</p>
                  </div>
                  <div>
                    <p className="text-[var(--text-muted)]">Driver Pay</p>
                    <p className="text-lg font-medium text-blue-400">${testCalculation.data.driver_earnings || testCalculation.data.estimated_driver_pay}</p>
                  </div>
                  <div>
                    <p className="text-[var(--text-muted)]">Platform Fee</p>
                    <p className="text-lg font-medium text-amber-400">${testCalculation.data.platform_fee}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Laundry Pricing Section */}
      <div className="space-y-3">
        <SectionHeader icon={Shirt} title="Laundry Pricing" section="laundry" color="purple" />
        {expandedSections.laundry && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg bg-[var(--card)] border border-[var(--border)] space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Wash & Fold Minimum"
                value={config.laundry?.wash_fold_minimum}
                onChange={(v) => updateConfigField('laundry.wash_fold_minimum', v)}
              />
              <PriceInput
                label="Per Pound Rate"
                value={config.laundry?.per_pound_rate}
                onChange={(v) => updateConfigField('laundry.per_pound_rate', v)}
                suffix="/lb"
              />
              <PriceInput
                label="Pickup Fee"
                value={config.laundry?.pickup_fee}
                onChange={(v) => updateConfigField('laundry.pickup_fee', v)}
              />
              <PriceInput
                label="Delivery Fee"
                value={config.laundry?.delivery_fee}
                onChange={(v) => updateConfigField('laundry.delivery_fee', v)}
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Bag Pricing</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PriceInput
                label="Small Bag"
                value={config.laundry?.per_bag_small}
                onChange={(v) => updateConfigField('laundry.per_bag_small', v)}
              />
              <PriceInput
                label="Medium Bag"
                value={config.laundry?.per_bag_medium}
                onChange={(v) => updateConfigField('laundry.per_bag_medium', v)}
              />
              <PriceInput
                label="Large Bag"
                value={config.laundry?.per_bag_large}
                onChange={(v) => updateConfigField('laundry.per_bag_large', v)}
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Special Items & Delivery</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Comforter Rate"
                value={config.laundry?.comforter_rate}
                onChange={(v) => updateConfigField('laundry.comforter_rate', v)}
              />
              <PriceInput
                label="Delicates Rate"
                value={config.laundry?.delicates_rate}
                onChange={(v) => updateConfigField('laundry.delicates_rate', v)}
                suffix="/lb"
              />
              <PriceInput
                label="Included Delivery Miles"
                value={config.laundry?.included_delivery_miles}
                onChange={(v) => updateConfigField('laundry.included_delivery_miles', v)}
                prefix=""
                suffix="mi"
              />
              <PriceInput
                label="Delivery Rate"
                value={config.laundry?.delivery_rate_per_mile}
                onChange={(v) => updateConfigField('laundry.delivery_rate_per_mile', v)}
                suffix="/mi"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={() => saveSection('laundry')} disabled={saving} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save Laundry Pricing
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Courier Pricing Section */}
      <div className="space-y-3">
        <SectionHeader icon={Package} title="Courier Pricing" section="courier" color="orange" />
        {expandedSections.courier && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg bg-[var(--card)] border border-[var(--border)] space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Base Fee"
                value={config.courier?.base_fee}
                onChange={(v) => updateConfigField('courier.base_fee', v)}
              />
              <PriceInput
                label="Included Miles"
                value={config.courier?.included_miles}
                onChange={(v) => updateConfigField('courier.included_miles', v)}
                prefix=""
                suffix="mi"
              />
              <PriceInput
                label="Per Mile Rate"
                value={config.courier?.per_mile_rate}
                onChange={(v) => updateConfigField('courier.per_mile_rate', v)}
                suffix="/mi"
              />
              <PriceInput
                label="Minimum Charge"
                value={config.courier?.minimum_charge}
                onChange={(v) => updateConfigField('courier.minimum_charge', v)}
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Stops & Waiting</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Per Additional Stop"
                value={config.courier?.per_stop_fee}
                onChange={(v) => updateConfigField('courier.per_stop_fee', v)}
              />
              <PriceInput
                label="Waiting Rate"
                value={config.courier?.waiting_rate_per_minute}
                onChange={(v) => updateConfigField('courier.waiting_rate_per_minute', v)}
                suffix="/min"
              />
              <PriceInput
                label="Included Waiting"
                value={config.courier?.included_waiting_minutes}
                onChange={(v) => updateConfigField('courier.included_waiting_minutes', v)}
                prefix=""
                suffix="min"
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Package Fees</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PriceInput
                label="Oversized Package"
                value={config.courier?.oversized_package_fee}
                onChange={(v) => updateConfigField('courier.oversized_package_fee', v)}
              />
              <PriceInput
                label="Heavy Package (>50 lbs)"
                value={config.courier?.heavy_package_fee}
                onChange={(v) => updateConfigField('courier.heavy_package_fee', v)}
              />
              <PriceInput
                label="Fragile Handling"
                value={config.courier?.fragile_handling_fee}
                onChange={(v) => updateConfigField('courier.fragile_handling_fee', v)}
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={() => saveSection('courier')} disabled={saving} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save Courier Pricing
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Errands Pricing Section */}
      <div className="space-y-3">
        <SectionHeader icon={ShoppingBag} title="Errands Pricing" section="errands" color="pink" />
        {expandedSections.errands && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg bg-[var(--card)] border border-[var(--border)] space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Base Fee"
                value={config.errands?.base_fee}
                onChange={(v) => updateConfigField('errands.base_fee', v)}
              />
              <PriceInput
                label="Included Radius"
                value={config.errands?.included_radius_miles}
                onChange={(v) => updateConfigField('errands.included_radius_miles', v)}
                prefix=""
                suffix="mi"
              />
              <PriceInput
                label="Per Mile Rate"
                value={config.errands?.per_mile_rate}
                onChange={(v) => updateConfigField('errands.per_mile_rate', v)}
                suffix="/mi"
              />
              <PriceInput
                label="Minimum Charge"
                value={config.errands?.minimum_charge}
                onChange={(v) => updateConfigField('errands.minimum_charge', v)}
              />
            </div>

            <h4 className="font-medium text-[var(--text-primary)] pt-2">Stops & Shopping</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PriceInput
                label="Per Additional Stop"
                value={config.errands?.per_stop_fee}
                onChange={(v) => updateConfigField('errands.per_stop_fee', v)}
              />
              <PriceInput
                label="Per Additional Store"
                value={config.errands?.per_store_fee}
                onChange={(v) => updateConfigField('errands.per_store_fee', v)}
              />
              <PriceInput
                label="Shopping Time Rate"
                value={config.errands?.shopping_time_rate}
                onChange={(v) => updateConfigField('errands.shopping_time_rate', v)}
                suffix="/min"
              />
              <PriceInput
                label="Included Shopping Time"
                value={config.errands?.included_shopping_minutes}
                onChange={(v) => updateConfigField('errands.included_shopping_minutes', v)}
                prefix=""
                suffix="min"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={() => saveSection('errands')} disabled={saving} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save Errands Pricing
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Business Settings Section */}
      <div className="space-y-3">
        <SectionHeader icon={Settings} title="Business Settings" section="business" color="gray" />
        {expandedSections.business && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg bg-[var(--card)] border border-[var(--border)] space-y-4"
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--background)]">
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">Require Upfront Payment for Guests</p>
                  <p className="text-xs text-[var(--text-muted)]">Customers without an account must pay online (no cash)</p>
                </div>
                <Switch
                  checked={config.business?.require_upfront_payment_guests}
                  onCheckedChange={(v) => updateConfigField('business.require_upfront_payment_guests', v)}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--background)]">
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">Allow Cash for Registered Customers</p>
                  <p className="text-xs text-[var(--text-muted)]">Customers with an account can choose cash payment</p>
                </div>
                <Switch
                  checked={config.business?.allow_cash_registered_customers}
                  onCheckedChange={(v) => updateConfigField('business.allow_cash_registered_customers', v)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <PercentInput
                  label="Low Margin Alert Threshold"
                  value={config.business?.low_margin_alert_threshold}
                  onChange={(v) => updateConfigField('business.low_margin_alert_threshold', v)}
                  info="Alert if job margin below this %"
                />
                <PercentInput
                  label="Platform Fee"
                  value={config.business?.platform_fee_percentage}
                  onChange={(v) => updateConfigField('business.platform_fee_percentage', v)}
                  info="Business platform/admin fee"
                />
                <PriceInput
                  label="Service Area Radius"
                  value={config.business?.service_area_radius_miles}
                  onChange={(v) => updateConfigField('business.service_area_radius_miles', v)}
                  prefix=""
                  suffix="mi"
                  info="Default service area"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => saveSection('business')} disabled={saving} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save Business Settings
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default PricingSettingsTab;
